import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChefHat, AlertCircle, RefreshCw, Check, Clock } from "lucide-react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";
import { useToast } from "@/hooks/use-toast";
import type { ProductTotals, ProductionSession } from "@shared/schema";

const REFETCH_INTERVAL = 5000;

export default function BagarePage() {
  const { toast } = useToast();
  const logoUrl = useCompanyLogo();
  const [workerId, setWorkerId] = useState<string>(() => {
    return localStorage.getItem("bagareId") || "";
  });

  const handleWorkerIdChange = (value: string) => {
    setWorkerId(value);
    localStorage.setItem("bagareId", value);
  };
  
  const { data: production, isFetching: productionFetching } = useQuery<ProductionSession | null>({
    queryKey: ["/api/production/active"],
    refetchInterval: REFETCH_INTERVAL,
  });

  const { data: totals, isLoading, isFetching: totalsFetching } = useQuery<ProductTotals[]>({
    queryKey: ["/api/production/totals"],
    enabled: !!production,
    refetchInterval: REFETCH_INTERVAL,
  });

  const markDoneMutation = useMutation({
    mutationFn: (productId: string) => apiRequest("POST", `/api/production/progress/${productId}/bagare`, { workerId }),
    onSuccess: () => {
      toast({ title: "Markerad som klar!" });
      queryClient.invalidateQueries({ queryKey: ["/api/production/totals"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte markera som klar.", variant: "destructive" });
    },
  });

  const isRefreshing = productionFetching || totalsFetching;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/production/active"] });
    queryClient.invalidateQueries({ queryKey: ["/api/production/totals"] });
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påsar";
      case "box": return "lådor";
      default: return unit;
    }
  };

  const calculateTotalPieces = (item: ProductTotals): number => {
    let totalPieces = item.totalQuantity;
    if (item.unit === "bag") {
      totalPieces = item.totalQuantity * item.pcsPerBag;
    } else if (item.unit === "box") {
      totalPieces = item.totalQuantity * item.pcsPerBag * item.bagsPerBox;
    }
    return totalPieces;
  };

  const allCompleted = totals?.every(t => t.bagareCompleted) ?? false;
  const completedCount = totals?.filter(t => t.bagareCompleted).length ?? 0;
  const totalCount = totals?.length ?? 0;

  if (!production) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-6">
          <div className="text-center">
            <img src={logoUrl} alt="Rätt Order" className="h-12 w-auto object-contain mb-4 mx-auto" />
            <h1 className="text-3xl font-bold">Bagare</h1>
            <p className="text-muted-foreground mt-2">Produktionsmängder</p>
          </div>
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Ingen aktiv produktion</p>
              <p className="text-sm text-muted-foreground mt-2">
                Vänta tills en admin startar produktion
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleRefresh}
                disabled={isRefreshing}
                data-testid="button-refresh"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
                Uppdatera
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="Rätt Order" className="h-10 w-auto object-contain" data-testid="img-production-logo" />
            <div>
              <h1 className="text-2xl font-bold">Bagare</h1>
              <p className="text-muted-foreground">Produktionsmängder att baka</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Uppdatera"
              data-testid="button-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
            {workerId && (
              <Badge variant="secondary">
                ID: {workerId}
              </Badge>
            )}
            {allCompleted ? (
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                Alla klara
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                {completedCount}/{totalCount} klara
              </Badge>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Ange ditt ID</CardTitle>
            <CardDescription>
              Ange ditt personal-ID för att registrera vem som arbetar med produkterna
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="workerId" className="sr-only">Bagare ID</Label>
                <Input
                  id="workerId"
                  placeholder="T.ex. 123 eller ditt namn"
                  value={workerId}
                  onChange={(e) => handleWorkerIdChange(e.target.value)}
                  data-testid="input-bagare-id"
                />
              </div>
              {workerId && (
                <Badge variant="secondary">
                  Aktiv: {workerId}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {isLoading ? (
            [1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="py-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-10 w-1/2" />
                </CardContent>
              </Card>
            ))
          ) : totals && totals.length > 0 ? (
            totals.map((item) => {
              const totalPieces = calculateTotalPieces(item);
              const canMarkDone = item.tragareCompleted && !item.bagareCompleted;
              const waitingForTragare = !item.tragareCompleted;
              
              return (
                <Card 
                  key={item.productId} 
                  className={item.bagareCompleted ? "border-green-500/50 bg-green-500/5" : ""}
                  data-testid={`card-product-${item.productId}`}
                >
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-lg">{item.productName}</p>
                          {item.bagareCompleted && (
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                              <Check className="h-3 w-3 mr-1" />
                              Klar
                            </Badge>
                          )}
                          {waitingForTragare && (
                            <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/20">
                              <Clock className="h-3 w-3 mr-1" />
                              Väntar på trågare
                            </Badge>
                          )}
                          {item.tragareCompleted && !item.bagareCompleted && (
                            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                              Redo att baka
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-6 text-sm">
                          <div>
                            <span className="text-muted-foreground">Antal: </span>
                            <span className="font-bold text-xl">{item.totalQuantity}</span>
                            <span className="text-muted-foreground ml-1">{getUnitLabel(item.unit)}</span>
                          </div>
                          
                          <div>
                            <span className="text-muted-foreground">Totalt: </span>
                            <span className="font-bold text-xl">{totalPieces}</span>
                            <span className="text-muted-foreground ml-1">st</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="shrink-0">
                        {item.bagareCompleted ? (
                          <Button variant="outline" disabled className="w-full sm:w-auto">
                            <Check className="h-4 w-4 mr-2" />
                            Klar
                          </Button>
                        ) : canMarkDone ? (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => markDoneMutation.mutate(item.productId)}
                              disabled={markDoneMutation.isPending || !workerId}
                              className="w-full sm:w-auto"
                              title={!workerId ? "Ange ditt ID först" : ""}
                              data-testid={`button-mark-done-${item.productId}`}
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Markera klar
                            </Button>
                            {!workerId && (
                              <p className="text-xs text-amber-600">Ange ID ovan</p>
                            )}
                          </div>
                        ) : (
                          <Button variant="outline" disabled className="w-full sm:w-auto">
                            <Clock className="h-4 w-4 mr-2" />
                            Väntar...
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Inga produkter i denna produktion
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
